import { Button, HStack, Text, VStack } from "@chakra-ui/react"
import type { BlocksChanges } from "@pathwright/blocks-engine"
import { CheckIcon } from "@pathwright/pathicons"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useEffect, useState } from "react"
import ContentPublishAudience from "./ContentPublishAudience"
import ContentPublishChanges from "./ContentPublishChanges"
import ContentPublishConfirmation from "./ContentPublishConfirmation"
import type { OnClickBlockChange } from "./ContentPublishDiff"
import useResponseWarning from "./useResponseWarning"

// Shared card body for document tools and Path Step publishing. Callers own
// document selection, permissions, and persistence.
const ContentPublishPanel = ({
  changes,
  needsPublish,
  audience,
  shared,
  checkResponses,
  busy,
  publishing,
  canPublish = true,
  onPublish,
  onDiscard,
  onRevert,
  onClickBlockChange,
  open = true
}: {
  changes?: BlocksChanges
  needsPublish: boolean
  audience: string
  shared: boolean
  checkResponses?: () => Promise<boolean>
  busy?: boolean
  publishing?: boolean
  canPublish?: boolean
  onPublish?: () => void
  onDiscard?: () => void
  onRevert?: () => Promise<void>
  onClickBlockChange?: OnClickBlockChange
  open?: boolean
}) => {
  const { tc } = useTranslate()
  const [confirmation, setConfirmation] = useState<"discard" | "revert" | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const { checking, hasResponses } = useResponseWarning(
    checkResponses,
    open && confirmation === "revert"
  )
  useEffect(() => {
    if (!open) setConfirmation(null)
  }, [open])

  const confirm = async () => {
    if (checking) return
    setError(null)
    if (confirmation === "discard") {
      onDiscard?.()
      setConfirmation(null)
    } else {
      try {
        await onRevert?.()
        setConfirmation(null)
      } catch (error) {
        setError(error instanceof Error ? error.message : String(error))
      }
    }
  }

  return (
    <VStack align="stretch" gap={4}>
      {confirmation ? (
        <ContentPublishConfirmation
          action={confirmation}
          cohortName={audience}
          showResponseWarning={
            confirmation === "revert" && !checking && hasResponses
          }
          busy={busy || checking}
          onConfirm={() => void confirm()}
          onCancel={() => setConfirmation(null)}
        />
      ) : (
        <>
          <ContentPublishChanges
            changes={changes}
            needsPublish={needsPublish}
            onDiscard={
              onDiscard && !busy ? () => setConfirmation("discard") : undefined
            }
            onClickBlockChange={onClickBlockChange}
          />
          <HStack
            gap={2}
            justify="flex-start"
            flexWrap="nowrap"
            w="max-content"
          >
            <Button
              colorPalette="brand"
              minW="128px"
              flexShrink={0}
              disabled={!needsPublish || !canPublish || busy}
              loading={publishing}
              loadingText={tc("Publishing...")}
              onClick={onPublish}
            >
              {!needsPublish && <CheckIcon size="16px" />}
              {needsPublish ? tc("Publish") : tc("Published")}
            </Button>
            <ContentPublishAudience
              name={audience}
              shared={shared}
              disabled={busy}
              onRevert={onRevert ? () => setConfirmation("revert") : undefined}
            />
          </HStack>
        </>
      )}
      {error && (
        <Text role="alert" color="red.600" fontSize="sm">
          {error}
        </Text>
      )}
    </VStack>
  )
}

export default ContentPublishPanel
